import * as React from 'react'

import { Box, CheckBox, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import TextInput from 'modules/core/components/Inputs/Text'
import MultiInput from 'modules/core/components/MultiInput'
import { type FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import { InformationTooltip } from 'modules/timeoff/components/TimeoffAllowances/UpsertAllowance/AllowanceSettings/_InformationTooltip'
import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import { availabilityOptionsForWorkedTimeLabels } from 'modules/timeoff/lib/dynamicTranslations'
import { AvailabilityOptionsForWorkedTime } from 'modules/timeoff/lib/enums'

interface Props {
  allowanceForm: AllowanceForm
  featureAvailabilities: FeatureAvailabilityType[]
}

const BaseOnTimeWorked = ({ allowanceForm, featureAvailabilities }: Props) => {
  const showFrequencyOptions = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_accrual_frequency_options
  )

  const availabilityOptions = Object.values(AvailabilityOptionsForWorkedTime).map((value) => ({
    label: availabilityOptionsForWorkedTimeLabels(value),
    value,
  }))

  const proratioValue = allowanceForm.get('pto_proratio_enabled').value

  const renderProratioOptions =
    hasFeature('dev_accrued_paid_timeoff_with_proratio') || proratioValue

  const onProrationEnabledChange = () => {
    const proratioEnabledValue = allowanceForm.get('pto_proratio_enabled')
    proratioEnabledValue.set(!proratioValue)

    if (proratioEnabledValue) {
      // We do not allow unlimited if proratio is enabled
      allowanceForm.get('unlimited_accrued_hours').set(false)
    }
  }

  return (
    <>
      {renderProratioOptions && (
        <Box flexDirection="row" gap="s6" alignItems="center">
          <TogglerCheckbox
            label={i18n.t('time_off.allowances.page.labels.pto_proratio_enabled')}
            value={proratioValue}
            onChange={onProrationEnabledChange}
          />
          <InformationTooltip
            label={i18n.t('time_off.allowances.page.information.pto_proratio_enabled')}
          />
        </Box>
      )}
      {!proratioValue && (
        <MultiInput>
          <TextInput
            label={i18n.t('time_off.allowances.page.labels.accrued_factor_in_cents')}
            field={allowanceForm.get('accrued_factor_in_cents')}
            mandatory
          />
          <TextInput
            label={i18n.t('time_off.allowances.page.labels.accrued_denominator_in_cents')}
            field={allowanceForm.get('accrued_denominator_in_cents')}
            mandatory
          />
        </MultiInput>
      )}
      <TextInput
        label={i18n.t('time_off.allowances.page.labels.maximum_amount_in_cents')}
        field={allowanceForm.get('maximum_amount_in_cents')}
        mandatory={!allowanceForm.values.unlimited_accrued_hours}
        readonly={allowanceForm.values.unlimited_accrued_hours}
        value={allowanceForm.values.unlimited_accrued_hours ? i18n.t('common.unlimited') : ''}
      />
      {!proratioValue && (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <CheckBox
            checked={allowanceForm.values.unlimited_accrued_hours}
            onClick={() =>
              allowanceForm.setValues({
                unlimited_accrued_hours: !allowanceForm.values.unlimited_accrued_hours,
              })
            }
          />
          <Text.Normal>{i18n.t('time_off.allowances.page.labels.unlimited_hours')}</Text.Normal>
        </Box>
      )}
      {showFrequencyOptions && (
        <Select
          field={allowanceForm.get('available_days')}
          label={i18n.t('time_off.allowances.page.labels.available_hours')}
          secondaryLabel={
            <InformationTooltip
              label={i18n.t('time_off.allowances.page.information.available_hours')}
            />
          }
          options={availabilityOptions}
        />
      )}
    </>
  )
}

export default observer(BaseOnTimeWorked)
